<template>
  <div
    class="content-rating"
    :class="{ 'content-rating--feedback-open': showFeedbackForm }"
  >
    <div class="rating-controls" :aria-label="label">
      <v-btn
        class="rating-icon-btn thumb-btn"
        :class="{ active: internalValue === 'up' }"
        icon="mdi-thumb-up-outline"
        variant="tonal"
        size="x-small"
        color="success"
        aria-label="Rate thumbs up"
        :disabled="loading || preview"
        @click="handleThumb('up')"
      />
      <v-btn
        class="rating-icon-btn thumb-btn"
        :class="{ active: internalValue === 'down' }"
        icon="mdi-thumb-down-outline"
        variant="tonal"
        size="x-small"
        color="error"
        aria-label="Rate thumbs down"
        :disabled="loading || preview"
        @click="handleThumb('down')"
      />
      <v-btn
        v-if="dismissible"
        class="rating-dismiss-btn"
        icon="mdi-close"
        variant="text"
        size="x-small"
        aria-label="Dismiss rating prompt"
        :title="dismissLabel"
        :disabled="loading"
        @click="$emit('dismiss')"
      />
    </div>
    <v-expand-transition>
      <div v-if="showFeedbackForm" class="rating-feedback-form">
        <div class="rating-feedback-title">What was wrong?</div>
        <template v-if="isGrouped">
          <div
            v-for="group in feedbackOptionGroups"
            :key="group.name"
            class="rating-feedback-group"
          >
            <div class="rating-feedback-group-label">{{ group.name }}</div>
            <div class="rating-feedback-options">
              <v-checkbox
                v-for="option in group.options"
                :key="option.value"
                v-model="selectedReasons"
                :label="option.label"
                :value="option.value"
                density="compact"
                hide-details
                color="error"
                :disabled="isReasonOptionDisabled(option.value)"
              />
            </div>
          </div>
        </template>
        <div v-else class="rating-feedback-options">
          <v-checkbox
            v-for="option in feedbackOptions"
            :key="option.value"
            v-model="selectedReasons"
            :label="option.label"
            :value="option.value"
            density="compact"
            hide-details
            color="error"
            :disabled="isReasonOptionDisabled(option.value)"
          />
        </div>
        <div
          v-if="feedbackOptions.length > MAX_RATING_FEEDBACK_REASONS"
          class="rating-feedback-limit text-caption text-medium-emphasis"
          aria-live="polite"
        >
          Choose up to {{ MAX_RATING_FEEDBACK_REASONS }} reasons.
        </div>
        <v-textarea
          v-model="feedbackComment"
          class="mt-2"
          label="Tell us what needs fixing — the more specific, the better."
          variant="outlined"
          density="compact"
          rows="2"
          auto-grow
          :disabled="loading"
          :readonly="preview"
          maxlength="1000"
          counter
        />
        <div class="rating-feedback-actions">
          <v-btn
            color="error"
            size="small"
            :disabled="selectedReasons.length === 0 || loading || preview"
            :loading="loading"
            @click="submitDownFeedback"
          >
            Submit feedback
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            :disabled="loading || preview"
            @click="cancelDownFeedback"
          >
            Cancel
          </v-btn>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { MAX_RATING_FEEDBACK_REASONS } from "@/utils/contentRatingFeedback.mjs";

const props = defineProps({
  label: {
    type: String,
    default: "Was this content helpful?",
  },
  subLabel: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
  dismissLabel: {
    type: String,
    default: "Not now",
  },
  feedbackOptions: {
    type: Array,
    default: () => [],
  },
  preview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["rate", "dismiss"]);
const internalValue = ref(props.preview ? "down" : null);
const showFeedbackForm = ref(props.preview);
const selectedReasons = ref([]);
const feedbackComment = ref("");

const hasFeedbackOptions = computed(() => props.feedbackOptions.length > 0);
const feedbackSelectionLimitReached = computed(
  () => selectedReasons.value.length >= MAX_RATING_FEEDBACK_REASONS,
);

const isReasonOptionDisabled = (value) =>
  props.loading ||
  props.preview ||
  (feedbackSelectionLimitReached.value &&
    !selectedReasons.value.includes(value));

const isGrouped = computed(() => props.feedbackOptions.some((o) => o.group));

const feedbackOptionGroups = computed(() => {
  const groups = [];
  const seen = new Map();
  for (const option of props.feedbackOptions) {
    const name = option.group || "";
    if (!seen.has(name)) {
      const group = { name, options: [] };
      seen.set(name, group);
      groups.push(group);
    }
    seen.get(name).options.push(option);
  }
  return groups;
});

const resetFeedbackForm = () => {
  showFeedbackForm.value = false;
  selectedReasons.value = [];
  feedbackComment.value = "";
};

const handleThumb = (value) => {
  if (!value || props.loading || props.preview) return;
  internalValue.value = value;

  if (value === "down" && hasFeedbackOptions.value) {
    showFeedbackForm.value = true;
    return;
  }

  resetFeedbackForm();
  emit("rate", { value, reasons: [], comment: "" });

  const resetValue = () => {
    internalValue.value = null;
  };

  if (typeof window !== "undefined" && window?.requestAnimationFrame) {
    window.requestAnimationFrame(resetValue);
  } else {
    resetValue();
  }
};

const submitDownFeedback = () => {
  if (props.loading || props.preview || selectedReasons.value.length === 0) {
    return;
  }
  emit("rate", {
    value: "down",
    reasons: selectedReasons.value,
    comment: feedbackComment.value,
  });
};

const cancelDownFeedback = () => {
  if (props.preview) return;
  resetFeedbackForm();
  internalValue.value = null;
};
</script>

<style scoped>
.content-rating {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
}

.content-rating--feedback-open {
  width: 100%;
  max-width: 480px;
}

.rating-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}

.rating-icon-btn,
.rating-dismiss-btn {
  flex: 0 0 auto;
}

.thumb-btn.active {
  box-shadow: 0 0 0 1px currentColor inset;
}

.rating-feedback-form {
  width: 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.rating-feedback-title {
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.rating-feedback-group {
  margin-bottom: 6px;
}

.rating-feedback-group-label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 2px;
}

.rating-feedback-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  column-gap: 10px;
}

.rating-feedback-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.rating-feedback-limit {
  margin-top: 4px;
}
</style>
